import { Reducer } from "react";
import type { University } from "../utils/utils";

// Define interfaces for better type safety
interface DistanceInfo {
  [key: string]: {
    distance: number;
    duration: string;
  };
}

interface RegionMarker {
  current: {
    setMap: (map: any) => void;
  } | null;
}

export interface TabItem {
  name: string;
  distance?: string;
  location?: {
    lat: number;
    lng: number;
  };
}

export interface Tab {
  name: string;
  items: TabItem[];
}

// Define the state interface
export interface MapState {
  // Search and options state
  options: University[];
  value: string;
  showOptions: boolean;
  secondRender: number;

  // University data
  universities: University[];
  distance: DistanceInfo;
  regionMarker: RegionMarker;
  selectedUniversityFromLocal: University | null;
  showDistanceCard: boolean;

  // Tabs state
  tabs: Tab[];
  activeTab: Tab | null;
  selectedOption: TabItem | null;
  isLoading: boolean;
  error: string | null;

  // Map state
  isMapScriptLoaded: boolean;
  mapInstance: any;
  mapCenter: any;
  isMapInitialized: boolean;
  isMapBuilt: boolean;
  inventoryMarker: any;
  nearbyLocationMarkers: any[];

  // Additional state for nearby locations
  nearbyLocation: any | null;
  nearbyLocations: any[];

  // Transport Data Strip
  transportDataStrip: any;
  // modal
  showModal: boolean;
  showMiles: boolean;
}

// Define the initial state
export const initialMapState: MapState = {
  // Search and options state
  options: [],
  value: "",
  showOptions: false,
  secondRender: 1,

  // University data
  universities: [],
  distance: {},
  regionMarker: { current: null },
  selectedUniversityFromLocal: null,
  showDistanceCard: false,

  // Tabs state
  tabs: [],
  activeTab: null,
  selectedOption: null,
  isLoading: false,
  error: null,

  // Map state
  isMapScriptLoaded: false,
  mapInstance: null,
  mapCenter: null,
  isMapInitialized: false,
  isMapBuilt: false,
  inventoryMarker: null,
  nearbyLocationMarkers: [],

  // Additional state for nearby locations
  nearbyLocation: null,
  nearbyLocations: [],

  // Transport Data Strip
  transportDataStrip: {},

  showModal: false,
  showMiles: true,
};

// Define action types
export type MapAction =
  // Search and options actions
  | { type: "SET_OPTIONS"; payload: University[] }
  | { type: "SET_VALUE"; payload: string }
  | { type: "SET_SHOW_OPTIONS"; payload: boolean }
  | { type: "SET_SECOND_RENDER"; payload: number }

  // University data actions
  | { type: "SET_UNIVERSITIES"; payload: University[] }
  | { type: "SET_DISTANCE"; payload: DistanceInfo }
  | { type: "SET_REGION_MARKER"; payload: RegionMarker }
  | { type: "SET_SELECTED_UNIVERSITY"; payload: University | null }
  | { type: "SET_SHOW_DISTANCE_CARD"; payload: boolean }

  // Tabs actions
  | { type: "SET_TABS"; payload: Tab[] }
  | { type: "SET_ACTIVE_TAB"; payload: Tab | null }
  | { type: "SET_SELECTED_OPTION"; payload: TabItem | null }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }

  // Map actions
  | { type: "SET_MAP_SCRIPT_LOADED"; payload: boolean }
  | { type: "SET_MAP_INSTANCE"; payload: any }
  | { type: "SET_MAP_CENTER"; payload: any }
  | { type: "SET_MAP_INITIALIZED"; payload: boolean }
  | { type: "SET_MAP_BUILT"; payload: boolean }
  | { type: "SET_INVENTORY_MARKER"; payload: any }
  | { type: "ADD_NEARBY_LOCATION_MARKER"; payload: any }
  | { type: "CLEAR_NEARBY_LOCATION_MARKERS" }

  // Additional actions for nearby locations
  | { type: "SET_NEARBY_LOCATION"; payload: any | null }
  | { type: "SET_NEARBY_LOCATIONS"; payload: any[] }

  //  Transportation strip
  | { type: "SET_TRANSPORT_DATA_STRIP"; payload: any }
  // Modal state
  | { type: "SHOW_MODAL"; payload: boolean }
  | { type: "SHOW_MILES"; payload: boolean }
  // Reset action
  | { type: "RESET_STATE" };

// Create the reducer
export const mapReducer: Reducer<MapState, MapAction> = (state, action) => {
  switch (action.type) {
    // Search and options cases
    case "SET_OPTIONS":
      return { ...state, options: action.payload };
    case "SET_VALUE":
      return { ...state, value: action.payload };
    case "SET_SHOW_OPTIONS":
      return { ...state, showOptions: action.payload };
    case "SET_SECOND_RENDER":
      return { ...state, secondRender: action.payload };

    // University data cases
    case "SET_UNIVERSITIES":
      return { ...state, universities: action.payload };
    case "SET_DISTANCE":
      return { ...state, distance: action.payload };
    case "SET_REGION_MARKER":
      return { ...state, regionMarker: action.payload };
    case "SET_SELECTED_UNIVERSITY":
      return { ...state, selectedUniversityFromLocal: action.payload };
    case "SET_SHOW_DISTANCE_CARD":
      return { ...state, showDistanceCard: action.payload };

    // Tabs cases
    case "SET_TABS":
      return { ...state, tabs: action.payload };
    case "SET_ACTIVE_TAB":
      return { ...state, activeTab: action.payload };
    case "SET_SELECTED_OPTION":
      return { ...state, selectedOption: action.payload };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };

    // Map cases
    case "SET_MAP_SCRIPT_LOADED":
      return { ...state, isMapScriptLoaded: action.payload };
    case "SET_MAP_INSTANCE":
      return { ...state, mapInstance: action.payload };
    case "SET_MAP_CENTER":
      return { ...state, mapCenter: action.payload };
    case "SET_MAP_INITIALIZED":
      return { ...state, isMapInitialized: action.payload };
    case "SET_MAP_BUILT":
      return { ...state, isMapBuilt: action.payload };
    case "SET_INVENTORY_MARKER":
      return { ...state, inventoryMarker: action.payload };
    case "ADD_NEARBY_LOCATION_MARKER":
      return {
        ...state,
        nearbyLocationMarkers: [...state.nearbyLocationMarkers, action.payload],
      };
    case "CLEAR_NEARBY_LOCATION_MARKERS":
      return { ...state, nearbyLocationMarkers: [] };

    // Additional cases for nearby locations
    case "SET_NEARBY_LOCATION":
      return { ...state, nearbyLocation: action.payload };
    case "SET_NEARBY_LOCATIONS":
      return { ...state, nearbyLocations: action.payload };
    case "SET_TRANSPORT_DATA_STRIP":
      return { ...state, transportDataStrip: action.payload };

    case "SHOW_MODAL":
      return { ...state, showModal: action.payload };
    case "SHOW_MILES":
      return { ...state, showMiles: action.payload };
    // Reset case
    case "RESET_STATE":
      return initialMapState;
    default:
      return state;
  }
};
